import { useTrainingPlansProgressContext } from "@/context/game/training-plans/training-plans-progress-context";
import VRPulseIndicator from "@Components/game/common/vr-pulse-indicator";
import {
  TrainingPlanProgressSegment,
  TrainingPlanProgressTrack,
  TrainingPlanStatusButton,
  TrainingPlanStreakIndicator,
} from "@Components/game/header/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { faCalendar, faFire } from "@fortawesome/pro-solid-svg-icons";
import { useTrainingPlans } from "@hooks/game/training-plans/use-training-plans";
import useI18n from "@i18n";
import { Box, Typography, useTheme } from "@mui/material";
import { matchPath, useLocation } from "react-router-dom";

const sessionResultsPatterns = [
  "/home/:section/results/:sessionId",
  "/activity/session/:sessionId",
  "/challenges/challenge/:challengeId/sessions/session/:sessionId",
  "/playlists/playlist/:playlistId/sessions/session/:sessionId",
  "/coach/360/scenario-editor/results/:sessionId",
];

const TrainingPlanStatusCapsule = () => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { shouldShowHeaderButton, headerStatus, shouldShowTrainingReminder, openFromHeader } = useTrainingPlans();
  const { trainingPlanModel } = useTrainingPlansProgressContext();

  if (!shouldShowHeaderButton) return null;

  const isDisabled = headerStatus === "loading";
  const isComplete = headerStatus === "complete";
  const requiredSessionsCount = trainingPlanModel?.requiredSessionsCount || 0;
  const completedSessionsCount = trainingPlanModel?.completedSessionsCount || 0;
  const shouldBeCompact = sessionResultsPatterns.some(pattern => matchPath({ path: pattern, end: true }, pathname));
  const progressWidth = requiredSessionsCount * 40 + Math.max(0, requiredSessionsCount - 1) * 6;

  let label: string;

  switch (headerStatus) {
    case "complete":
      label = i18n("training-plans.header.complete");
      break;
    case "active":
      label = i18n("training-plans.header.progress", {
        completed: completedSessionsCount,
        required: requiredSessionsCount,
      });
      break;
    case "not-started":
      label = i18n("training-plans.header.start");
      break;
    case "loading":
      label = i18n("training-plans.header.loading");
      break;
    default:
      label = i18n("training-plans.header.unavailable");
  }

  const ariaLabel = trainingPlanModel?.showStreak
    ? i18n("training-plans.header.label-with-streak", { status: label, count: trainingPlanModel.currentStreakCount })
    : i18n("training-plans.header.label", { status: label });

  return (
    <HapticsButtonWrapper disabled={isDisabled}>
      <TrainingPlanStatusButton aria-label={ariaLabel} disabled={isDisabled} disableRipple onClick={openFromHeader}>
        <Box sx={{ alignItems: "center", display: "flex", height: 24, justifyContent: "center", position: "relative", width: 24 }}>
          <Icon icon={faCalendar} color={theme.palette.dsBase.v200} fontSize={24} />
          {shouldShowTrainingReminder && (
            <VRPulseIndicator color={theme.palette.dsYellow?.v200 ?? theme.palette.dsWarning.v300} offset={-4} size={8} />
          )}
        </Box>

        {headerStatus === "not-started" && !shouldBeCompact && (
          <Typography component="span" variant="textBase600" sx={{ color: theme.palette.dsNeutral.v300, lineHeight: 1 }}>
            {label}
          </Typography>
        )}

        {requiredSessionsCount > 0 && (
          <Box
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              overflow: "hidden",
              width: shouldBeCompact ? 0 : progressWidth,
              transition: theme.transitions.create("width", {
                duration: theme.transitions.duration.standard,
                easing: theme.transitions.easing.easeInOut,
              }),
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <TrainingPlanProgressTrack>
              {Array.from({ length: requiredSessionsCount }, (_, index) => (
                <TrainingPlanProgressSegment completed={index < completedSessionsCount} key={index} />
              ))}
            </TrainingPlanProgressTrack>
          </Box>
        )}

        {trainingPlanModel?.showStreak && (
          <TrainingPlanStreakIndicator achieved={isComplete} aria-hidden="true">
            <Icon icon={faFire} color="currentColor" fontSize={20} />
            <Typography component="span" variant="textBase600" sx={{ color: "inherit", lineHeight: 1 }}>
              {trainingPlanModel.currentStreakCount}
            </Typography>
          </TrainingPlanStreakIndicator>
        )}
      </TrainingPlanStatusButton>
    </HapticsButtonWrapper>
  );
};

export default TrainingPlanStatusCapsule;
